import { useEffect, useState } from 'react';

import { SentIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { MESSAGE_STATE_ERROR } from 'constants/message-app-state-contants';
import Moment from 'react-moment';
import { useDispatch } from 'react-redux';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { IconButton } from 'components/IconButton/IconButton';
import { Table } from 'components/Table/Table';

import { messagePopUp } from 'api/app-slice';
import {
  useLazyGetPartnerWebhookLogsQuery,
  useResendPartnerWebhookLogMutation,
} from 'api/partner-webhook-logs-api';

import styles from './WebhookLogs.module.scss';

const formatPayload = payload => {
  if (!payload) return '';
  if (typeof payload === 'string') {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }
  return JSON.stringify(payload, null, 2);
};

const statusCodeClass = code => {
  if (code === 'XXX') return 'text-error';
  const num = parseInt(code, 10);
  if (num >= 200 && num < 300) return 'text-success';
  if (num >= 400 && num < 500) return 'text-warning';
  return 'text-error';
};

const requestErrorMessage = error =>
  String(error?.data?.message ?? error?.data?.error ?? error?.message ?? 'Unknown error');

export const WebhookLogs = ({ setLoading = () => {} }) => {
  const dispatch = useDispatch();
  const [getLogs] = useLazyGetPartnerWebhookLogsQuery();
  const [resendLog] = useResendPartnerWebhookLogMutation();

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });

  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [payloadTitle, setPayloadTitle] = useState('');
  const [payloadText, setPayloadText] = useState('');

  useEffect(() => {
    search({ page: 1 });
  }, []);

  const search = async params => {
    try {
      setLoading(true);
      const { partner_webhook_logs, pagination: nextPagination } = await getLogs(params).unwrap();
      setLogs(partner_webhook_logs);
      setPagination(nextPagination);
    } catch (error) {
      console.error(error);
      dispatch(
        messagePopUp({
          text: requestErrorMessage(error),
          state: MESSAGE_STATE_ERROR,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const openPayload = (title, payload) => {
    setPayloadTitle(title);
    setPayloadText(formatPayload(payload));
    setShowPayloadModal(true);
  };

  const handleResend = async id => {
    try {
      setLoading(true);
      await resendLog(id).unwrap();
    } catch (error) {
      console.error(error);
      dispatch(
        messagePopUp({
          text: requestErrorMessage(error),
          state: MESSAGE_STATE_ERROR,
          hide: true,
        }),
      );
      return;
    } finally {
      setLoading(false);
    }
    await search({ page: 1 });
  };

  return (
    <div className={cn('d-flex', 'justify-content-center')}>
      <AnimatedModal
        visible={showPayloadModal}
        onClose={() => setShowPayloadModal(false)}
        title={payloadTitle}
        large
        noPadding>
        <div className="u-padding">
          <pre className={styles.jsonBlock}>{payloadText}</pre>
        </div>
      </AnimatedModal>

      <div className={cn('card--large', 'card', 'card-with-border', 'card-with-hidden-overflow')}>
        <div className={cn('u-padding--large', 'u-divider-bottom-border')}>
          <h1 className="title-2">Webhook Logs</h1>
          <p className="t-small u-margin-0">
            View the history of outbound webhook calls to your connected partner systems.
          </p>
        </div>
        <div className={cn('background-secondary', 'u-padding')}>
          <Table.Context defaultQuery={{ page: 1, search: '' }} onQueryUpdate={search}>
            <Table.TableContent>
              <Table.Filters>
                <Table.LeftFilters>
                  <Table.Search placeholder="Search by ID, URI, status..." />
                </Table.LeftFilters>
              </Table.Filters>
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Header noBorder>Datetime</Table.Header>
                    <Table.Header noBorder>Application ID</Table.Header>
                    <Table.Header noBorder>URI</Table.Header>
                    <Table.Header noBorder>Status</Table.Header>
                    <Table.Header noBorder>Request</Table.Header>
                    <Table.Header noBorder>Response</Table.Header>
                    <Table.Header noBorder></Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {logs.map(log => (
                    <Table.Row key={log.id}>
                      <Table.Col>
                        <Moment format="MMM Do @h:mm:ss a">{log.created_at}</Moment>
                      </Table.Col>
                      <Table.Col>{log.partner_application_id}</Table.Col>
                      <Table.Col>{log.uri}</Table.Col>
                      <Table.Col>
                        <b className={statusCodeClass(log.status_code)}>{log.status_code}</b>
                      </Table.Col>
                      <Table.Col>
                        <IconButton
                          icon="file-text"
                          onClick={() => openPayload('Request Payload', log.request_payload)}
                        />
                      </Table.Col>
                      <Table.Col>
                        {log.response_payload && (
                          <IconButton
                            icon="file-text"
                            onClick={() => openPayload('Response', log.response_payload)}
                          />
                        )}
                      </Table.Col>
                      <Table.Col>
                        <ActionsMenu newIconLibrary>
                          <ActionsMenu.Item
                            newIconLibrary
                            icon={SentIcon}
                            title="Resend"
                            disabled={!log.partner_webhook_id}
                            onClick={() => handleResend(log.id)}
                          />
                        </ActionsMenu>
                      </Table.Col>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Pagination {...pagination} />
              </Table>
            </Table.TableContent>
          </Table.Context>
        </div>
      </div>
    </div>
  );
};
